import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { FormComponent, FormComponentProps } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Hidden from '../../../../components/form/elements/hidden';
import Readonly from '../../../../components/form/elements/readonly';
import Select, {
  emptySelectOption,
} from '../../../../components/form/elements/select';
import Text from '../../../../components/form/elements/text';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import ClassifierValueType from '../../../../enums/classifier-value-type';
import { Unit } from '../../../../enums/unit';
import { YearSeason } from '../../../../enums/year-season';
import FertilizerWithManureCorrections from '../../../../interfaces/fertilizer-with-manure-corrections';
import { ListParams } from '../../../../services/http-client';
import { useService } from '../../../../services/services';
import { uuid } from '../../../../services/utils';
import css from './fertilizers.module.css';

type ComponentsStateType = {
  WorkId: FormComponent;
  WorkDone: FormComponent;
  Type: FormComponent;
  Fertilizer: FormComponent;
  Amount: FormComponent;
  YearSeason: FormComponent;
  Composition: {
    S: FormComponent;
    N: FormComponent;
    P2O5: FormComponent;
    K2O: FormComponent;
  };
};

interface Props extends FormComponentProps {
  updateCallback: (w: WatchType) => void;
  formValues?: any;
}

export default function Fertilizers(
  updateCallback: (w: WatchType) => void
): FormComponent {
  return ({ ...rest }) => (
    <FertilizersComponent updateCallback={updateCallback} {...rest} />
  );
}

export function FertilizersComponent(props: Props) {
  const { t } = useTranslation();

  const transKeyPrefix = 'pages.crop.fertilization-plan.plan.';

  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const params = useParams<{ farmId: string; season: string }>();

  const form = useFormContext();

  const { classifierService, fertilizationPlanService } = useService();

  const parentName = (rowId: string) => 'fertilizers'.concat('.', rowId);

  const workIdHidden = useCallback(() => Hidden('work_id'), []);

  const workDoneHidden = useCallback(() => Hidden('work_done'), []);

  const typeSelect = useCallback(
    () =>
      Watch(
        Select('type', {
          asyncOptions: () =>
            classifierService
              .values(
                ClassifierGroup.WAREHOUSE,
                Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                { record_type: ClassifierValueType.FERTILIZERS }
              )
              .pipe(
                switchMap((categories) =>
                  classifierService
                    .values(
                      ClassifierGroup.WAREHOUSE,
                      Classifier.PRODUCT_TYPES,
                      {
                        parent_id: categories[0].id,
                      }
                    )
                    .pipe(
                      map((types) => ({
                        hasMore: false,
                        additional: {},
                        options: types.map((v) => ({
                          value: v.id,
                          label: v.value,
                        })),
                      }))
                    )
                )
              ),
          inline: true,
          clearable: false,
        }),
        (w) => {
          const fertilizerName = w.parentName.concat('.fertilizer');
          const fertilizerTypeId = w.getValues(fertilizerName)?.type?.id;
          if (!fertilizerTypeId || fertilizerTypeId !== w.value[0].value) {
            w.setValue(w.parentName.concat('.fertilizer'), emptySelectOption);
          }
          props.updateCallback(w);
        }
      ),
    [classifierService, props]
  );

  const fertilizerSelect = useCallback(
    () =>
      Watch(
        Select('fertilizer', {
          asyncOptions: (value, _, { page, parentName, getValues }) => {
            const search: ListParams = {
              page,
              value,
              [ClassifierPropertyKey.YEAR]: 1,
            };
            const typeId = getValues(parentName.concat('.type'))?.value;
            if (typeId) {
              search[ClassifierPropertyKey.FERTILIZER_TYPE] = typeId;
            }
            const mapOptions = (
              fertilizers: FertilizerWithManureCorrections[]
            ) =>
              fertilizers.map((fertilizer) => ({
                value: fertilizer.id,
                label: fertilizer.value,
                type: {
                  id: fertilizer.type_id,
                  label: fertilizer.type_label,
                },
                s: fertilizer.s,
                n: fertilizer.n,
                p2o5: fertilizer.p2o5,
                k2o: fertilizer.k2o,
                n_correction: fertilizer.n_correction,
                p2o5_correction: fertilizer.p2o5_correction,
                k2o_correction: fertilizer.k2o_correction,
              }));
            return forkJoin([
              search.page > 1
                ? of(null)
                : fertilizationPlanService.usedFertilizersWithManureCorrections(
                    params.farmId,
                    params.season,
                    search
                  ),
              classifierService.fertilizersWithManureCorrections(search),
            ]).pipe(
              map(([usedFertilizers, fertilizers]) => ({
                hasMore: fertilizers.last_page > fertilizers.current_page,
                additional: { page: fertilizers.current_page + 1 },
                options:
                  page > 1
                    ? mapOptions(fertilizers.data)
                    : [
                        {
                          label: tp('used_fertilizers'),
                          options: mapOptions(usedFertilizers),
                        },
                        {
                          label: tp('all_fertilizers'),
                          options: mapOptions(fertilizers.data),
                        },
                      ],
              }))
            );
          },
          inline: true,
          clearable: false,
          cacheOn: ['type'],
        }),
        (w) => {
          if (!w.value[0].value) {
            return;
          }
          const typeName = w.parentName.concat('.type');
          const typeId = w.getValues(typeName)?.value;
          if (typeId !== w.value[0].type.id) {
            w.setValue(typeName, {
              value: w.value[0].type.id,
              label: w.value[0].type.label,
            });
          }
          props.updateCallback(w);
        }
      ),
    [
      classifierService,
      fertilizationPlanService,
      params.farmId,
      params.season,
      props,
      tp,
    ]
  );

  const amountAmount = useCallback(
    () =>
      Watch(
        Amount(
          { amount: 'amount', unit: 'amount_unit' },
          {
            inline: true,
            onDisabled: 'both',
            disableUnit: true,
            options: [{ value: Unit.KILOGRAM, label: Unit.KILOGRAM }],
          }
        ),
        (w) => props.updateCallback(w)
      ),
    [props]
  );

  const yearSeasonSelect = useCallback(
    () =>
      Select('year_season', {
        options: [
          { label: tp(YearSeason.SPRING), value: YearSeason.SPRING },
          { label: tp(YearSeason.AUTUMN), value: YearSeason.AUTUMN },
        ],
        inline: true,
        clearable: false,
      }),
    [tp]
  );

  const sText = useCallback(
    () => Readonly(Text('s', null, null, { inline: true })),
    []
  );

  const nText = useCallback(
    () => Readonly(Text('n', null, null, { inline: true })),
    []
  );

  const p2o5Text = useCallback(
    () => Readonly(Text('p2o5', null, null, { inline: true })),
    []
  );

  const k2oText = useCallback(
    () => Readonly(Text('k2o', null, null, { inline: true })),
    []
  );

  const componentFactory = useCallback(
    () =>
      ({
        WorkId: workIdHidden(),
        WorkDone: workDoneHidden(),
        Type: typeSelect(),
        Fertilizer: fertilizerSelect(),
        Amount: amountAmount(),
        YearSeason: yearSeasonSelect(),
        Composition: {
          S: sText(),
          N: nText(),
          P2O5: p2o5Text(),
          K2O: k2oText(),
        },
      } as ComponentsStateType),
    [
      amountAmount,
      fertilizerSelect,
      k2oText,
      nText,
      p2o5Text,
      sText,
      typeSelect,
      workIdHidden,
      workDoneHidden,
      yearSeasonSelect,
    ]
  );

  const [rows, setRows] = useState<
    { id: string; workDone: boolean; Components: ComponentsStateType }[]
  >([]);

  const add = () => {
    const id = uuid();
    setRows((state) =>
      state.concat({
        id: id,
        workDone: false,
        Components: componentFactory(),
      })
    );
    form.setValue(`fertilizers.${id}.amount_unit`, {
      value: Unit.KILOGRAM,
      label: Unit.KILOGRAM,
    });
  };

  const remove = (id: string) => {
    setRows((state) => {
      return state.filter((row) => row.id !== id);
    });
    const fertilizers = form.getValues(`fertilizers`);
    delete fertilizers[id];
    form.setValue(`fertilizers`, fertilizers);
  };

  useEffect(() => {
    if (rows.length === 0 && props.formValues) {
      setRows(
        Object.keys(props.formValues['fertilizers']).map((id) => ({
          id: id,
          workDone: props.formValues['fertilizers'][id]['work_done'],
          Components: componentFactory(),
        }))
      );
    }
  }, [componentFactory, props.formValues, rows.length]);

  useEffect(() => {
    if (rows.length > 0) {
      props.updateCallback({
        setValue: form.setValue,
        getValues: form.getValues,
        parentName: null,
      } as WatchType);
    }
  }, [form.getValues, form.setValue, props, rows]);

  return (
    <table className={css['fertilizers']}>
      <thead>
        <tr>
          <th colSpan={2}>
            {tp('fertilizer')}
            <span className={css['required']}>*</span>
          </th>
          <th>
            {tp('dose')}
            <span className={css['required']}>*</span>
          </th>
          <th>
            {tp('year_season')}
            <span className={css['required']}>*</span>
          </th>
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((Row, index) => (
          <tr key={Row.id} className={css['row']}>
            <td className={css['type']}>
              <Row.Components.WorkId parentName={parentName(Row.id)} />
              <Row.Components.WorkDone parentName={parentName(Row.id)} />
              <Row.Components.Type
                parentName={parentName(Row.id)}
                readonly={props.readonly || Row.workDone}
              />
            </td>
            <td className={css['fertilizer']}>
              <Row.Components.Fertilizer
                parentName={parentName(Row.id)}
                readonly={props.readonly || Row.workDone}
              />
            </td>
            <td className={css['amount']}>
              <Row.Components.Amount
                parentName={parentName(Row.id)}
                readonly={props.readonly || Row.workDone}
              />
            </td>
            <td className={css['yearSeason']}>
              <Row.Components.YearSeason
                parentName={parentName(Row.id)}
                readonly={props.readonly || Row.workDone}
              />
            </td>
            <td className={css['remove']}>
              {props.readonly || Row.workDone || rows.length === 1 || (
                <button type="button" onClick={() => remove(Row.id)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </td>
            <td className={css['composition']}>
              <Row.Components.Composition.S parentName={parentName(Row.id)} />
            </td>
            <td className={css['composition']}>
              <Row.Components.Composition.N parentName={parentName(Row.id)} />
            </td>
            <td className={css['composition']}>
              <Row.Components.Composition.P2O5
                parentName={parentName(Row.id)}
              />
            </td>
            <td className={css['composition']}>
              <Row.Components.Composition.K2O parentName={parentName(Row.id)} />
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className={css['add']}>
          <td colSpan={8}>
            {props.readonly || (
              <button type="button" onClick={() => add()}>
                <FontAwesomeIcon icon={faPlus} /> {tp('add_fertilizer')}
              </button>
            )}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
